import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createUserApi,
  updateUserApi,
  patchUserApi,
  deleteUserApi,
  deleteUsersApi,
  updateUserRolesApi,
} from '../api/employeeApi'
import { type User } from '../../../utils/types'
import LogTool from '../../../logger/logTools'
import { updatePreferencesApi } from '../../Profile/api/profileApi'
import { userKeys } from '../utils/queryKeys'

const log = new LogTool({ context: 'EmployeeMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new user
 */
export function useCreateUser(): UseMutationResult<User, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createUserApi,
    onSuccess: async () => {
      // Invalidate all user list queries (both old and new query key formats)
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      // Invalidate user search queries
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      log.info('User created successfully')
    },
    onError: (error) => {
      log.error('Error creating user:', error)
    },
  })
}

/**
 * Hook to update a user (full update with PUT)
 */
export function useUpdateUser(): UseMutationResult<
  User,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateUserApi(idOrSelf, data),
    onSuccess: async (updatedUser) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (roles, sub_roles, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      // Invalidate user search queries
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.detail(updatedUser.key)
      })
      log.info('User updated successfully')
    },
    onError: (error) => {
      log.error('Error updating user:', error)
    },
  })
}

/**
 * Hook to patch a user (partial update with PATCH)
 */
export function usePatchUser(): UseMutationResult<
  User,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => patchUserApi(idOrSelf, data),
    onSuccess: async (updatedUser) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (roles, sub_roles, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.detail(updatedUser.key)
      })
      log.info('User patched successfully')
    },
    onError: (error) => {
      log.error('Error patching user:', error)
    },
  })
}

/**
 * Hook to delete a single user
 */
export function useDeleteUser(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteUserApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      log.info('User deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting user:', error)
    },
  })
}

/**
 * Hook to delete multiple users
 */
export function useDeleteUsers(): UseMutationResult<void, Error, User[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteUsersApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      log.info('Users deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting users:', error)
    },
  })
}

/**
 * Hook to update user roles
 */
export function useUpdateUserRoles(): UseMutationResult<
  void,
  Error,
  { userIdOrSelf: string | number; roles: any[] }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userIdOrSelf, roles }) => updateUserRolesApi(userIdOrSelf, roles),
    onSuccess: async (_, variables) => {
      // Invalidate to reload with expanded data (roles with full details)
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.detail(variables.userIdOrSelf)
      })
      log.info('User roles updated successfully')
    },
    onError: (error) => {
      log.error('Error updating user roles:', error)
    },
  })
}

/**
 * Hook to update current user (self)
 */
export function useUpdateCurrentUser(): UseMutationResult<User, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data) => patchUserApi('self', data),
    onSuccess: async (updatedUser) => {
      // Update cache for user lists if the updated user is in there
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      queryClient.setQueriesData(
        { queryKey: userKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: User) =>
              (item.key === updatedUser.key || item.email === updatedUser.email) ? updatedUser : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.self(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: userKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: ['user-search'],
        refetchType: 'active'
      })
      log.info('Current user updated successfully')
    },
    onError: (error) => {
      log.error('Error updating current user:', error)
    },
  })
}

/**
 * Hook to delete current user (self)
 */
export function useDeleteCurrentUser(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteUserApi('self'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.self() })
      log.info('Current user deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting current user:', error)
    },
  })
}

/**
 * Hook to update current user preferences
 */
export function useUpdateCurrentUserPreferences(): UseMutationResult<any, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (preferences) => {
      // Delegate to Profile API for preferences
      return updatePreferencesApi(preferences)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.self() })
      await queryClient.invalidateQueries({ queryKey: ['preferences'] })
      log.info('User preferences updated successfully')
    },
    onError: (error) => {
      log.error('Error updating user preferences:', error)
    },
  })
}

